import type { IconButtonProps } from "@mui/material/IconButton"

import { useState, useCallback } from "react"

import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Drawer from "@mui/material/Drawer"
import { useTheme } from "@mui/material/styles"
import Typography from "@mui/material/Typography"
import IconButton from "@mui/material/IconButton"

import { useRouter, usePathname } from "src/routes/hooks"
import { paths } from "src/routes/paths"

import { varAlpha } from "src/theme/styles"

import { Iconify } from "src/common/iconify"
import { AnimateAvatar } from "src/common/animate"

import { useAuthStore } from "@/src/store"

import { AccountButton } from "./account-button"
import { SignOutButton } from "./sign-out-button"
import { WorkspaceOptions } from "../dashboard/workspaceOptions"

// ----------------------------------------------------------------------

export type AccountDrawerProps = IconButtonProps & {
    data?: {
        label: string
        href: string
        icon?: React.ReactNode
        info?: React.ReactNode
    }[]
    isExpanded: boolean
    /** When true, hide workspace logo in collapsed sidebar (e.g. for internal users) */
    hideWorkspaceLogo?: boolean
}

export function AccountDrawer({ data = [], sx, isExpanded, hideWorkspaceLogo = false, ...other }: AccountDrawerProps) {
    const theme = useTheme()

    const router = useRouter()

    const pathname = usePathname()

    const user = useAuthStore((state) => state.user)

    const isInternalAdminPanel = pathname?.startsWith(paths.internalUser.root) ?? false

    const [open, setOpen] = useState(false)

    const handleOpenDrawer = useCallback(() => {
        setOpen(true)
    }, [])

    const handleCloseDrawer = useCallback(() => {
        setOpen(false)
    }, [])

    const handleClickItem = useCallback(
        (path: string) => {
            handleCloseDrawer()
            router.push(path)
        },
        [handleCloseDrawer, router]
    )

    const renderAvatar = (
        <AnimateAvatar
            width={96}
            slotProps={{
                avatar: { src: user?.profileImageUrl ?? "", alt: user?.fullName },
                overlay: {
                    border: 2,
                    spacing: 3,
                    color: `linear-gradient(135deg, ${varAlpha(theme.vars.palette.primary.mainChannel, 0)} 25%, ${theme.vars.palette.primary.main} 100%)`,
                },
            }}
        >
            {user?.fullName?.charAt(0).toUpperCase()}
        </AnimateAvatar>
    )

    return (
        <>
            <AccountButton
                open={open}
                onClick={handleOpenDrawer}
                photoURL={user?.profileImageUrl ?? ""}
                displayName={user?.fullName ?? ""}
                sx={sx}
                isExpanded={isExpanded}
                hideWorkspaceLogo={hideWorkspaceLogo}
                {...other}
            />

            <Drawer
                open={open}
                onClose={handleCloseDrawer}
                anchor="left"
                slotProps={{ backdrop: { invisible: true } }}
                PaperProps={{ sx: { width: 320 } }}
            >
                <IconButton onClick={handleCloseDrawer} sx={{ top: 12, left: 12, zIndex: 9, position: "absolute" }}>
                    <Iconify icon="mingcute:close-line" />
                </IconButton>

                <Stack alignItems="center" sx={{ pt: 8 }}>
                    {renderAvatar}

                    <Typography variant="subtitle1" noWrap sx={{ mt: 2 }}>
                        {user?.fullName}
                    </Typography>

                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }} noWrap>
                        {user?.email ?? ""}
                    </Typography>
                </Stack>

                {!isInternalAdminPanel && <WorkspaceOptions />}

                <Box sx={{ p: 2.5 }}>
                    <SignOutButton onClose={handleCloseDrawer} />
                </Box>
            </Drawer>
        </>
    )
}
